import { useState } from "react";
import { toast } from "sonner";
import { FolderInput, Tag, Check, Plus } from "lucide-react";
import { api, errText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { TagChip } from "@/components/library/TagChip";

export function MoveToMenu({ folders, currentFolderId, onMove, testId, label, className }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={testId} className={className} title="Move to folder">
          <FolderInput className="w-4 h-4" />{label && <span className="ml-2">{label}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-sm min-w-[200px]">
        <DropdownMenuLabel className="font-mono-label text-muted-foreground">Move to</DropdownMenuLabel>
        <DropdownMenuItem data-testid={`${testId}-none`} onClick={() => onMove(null)} className="flex justify-between">
          Unfiled {currentFolderId == null && <Check className="w-3.5 h-3.5" />}
        </DropdownMenuItem>
        {folders.length > 0 && <DropdownMenuSeparator />}
        {folders.map((f) => (
          <DropdownMenuItem key={f.id} data-testid={`${testId}-${f.id}`} onClick={() => onMove(f.id)} className="flex justify-between">
            <span className="truncate">{f.name}</span> {currentFolderId === f.id && <Check className="w-3.5 h-3.5" />}
          </DropdownMenuItem>
        ))}
        {folders.length === 0 && <p className="px-2 py-1.5 text-xs text-muted-foreground">No folders yet — add one in the sidebar.</p>}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TagEditor({ doc, allTags, onSaved }) {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const tags = doc.tags || [];

  const save = async (next) => {
    setSaving(true);
    try { await api.patch(`/documents/${doc.id}`, { tags: next }); onSaved(doc.id, next); }
    catch (err) { toast.error(errText(err, "Couldn't save tags")); }
    finally { setSaving(false); }
  };
  const add = (name) => {
    const n = name.trim().replace(/^#/, "");
    if (!n) return;
    if (tags.some((t) => t.toLowerCase() === n.toLowerCase())) return setDraft("");
    if (tags.length >= 10) return toast.error("Up to 10 tags per document");
    setDraft("");
    save([...tags, n]);
  };
  const suggestions = allTags.filter((t) => !tags.some((x) => x.toLowerCase() === t.toLowerCase()) && t.toLowerCase().includes(draft.trim().toLowerCase())).slice(0, 6);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`doc-tags-${doc.id}`} title="Tags"><Tag className="w-4 h-4" /></Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="rounded-sm w-72 p-3 space-y-3" data-testid={`tag-editor-${doc.id}`}>
        <div className="font-mono-label text-muted-foreground">Tags for {doc.filename.length > 26 ? doc.filename.slice(0, 26) + "…" : doc.filename}</div>
        <div className="flex flex-wrap gap-1.5 min-h-[24px]">
          {tags.length === 0 && <span className="text-xs text-muted-foreground">No tags yet</span>}
          {tags.map((t) => <TagChip key={t} name={t} testId={`tag-chip-${doc.id}-${t}`} onRemove={() => save(tags.filter((x) => x !== t))} />)}
        </div>
        <div className="flex gap-1">
          <Input data-testid={`tag-input-${doc.id}`} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Add a tag and press Enter"
            disabled={saving} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(draft); } }} className="h-8 rounded-sm text-sm" />
          <Button size="sm" data-testid={`tag-add-${doc.id}`} onClick={() => add(draft)} disabled={saving || !draft.trim()} className="h-8 rounded-sm bg-foreground text-background hover:bg-primary"><Plus className="w-3.5 h-3.5" /></Button>
        </div>
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((t) => <TagChip key={t} name={t} testId={`tag-suggest-${doc.id}-${t}`} onClick={() => add(t)} />)}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
